import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';
import { useAuth } from './AuthContext';

export interface ServiceAssignment {
  service_slug: string;
  service_name: string;
  service_category?: string;
  is_free: boolean;
  effective_price: number;
  is_active: boolean;
}

interface ServiceAccessValue {
  ready: boolean;
  bySlug: Record<string, ServiceAssignment>;
  canAccess: (slug?: string) => boolean;
  getAssignment: (slug?: string) => ServiceAssignment | undefined;
}

const ServiceAccessContext = createContext<ServiceAccessValue | undefined>(undefined);

export const ServiceAccessProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [assignments, setAssignments] = useState<ServiceAssignment[]>([]);
  const [ready, setReady] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);

  const corporateId = user?.corporate?.id ?? user?.corporate_info?.id ?? null;
  // Individuals: the same endpoint returns what their plan unlocks.
  const isIndividual = user?.account_type === 'individual';
  const lookupKey = corporateId ?? (isIndividual ? `individual-${user?.id}` : null);

  useEffect(() => {
    if (authLoading) return;

    if (!lookupKey) {
      setAssignments([]);
      setLookupFailed(false);
      setReady(true);
      return;
    }

    let cancelled = false;
    setReady(false);
    setLookupFailed(false);

    axiosInstance
      .get(API_ENDPOINTS.myServices)
      .then((res) => {
        if (cancelled) return;
        setAssignments(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setAssignments([]);
        setLookupFailed(true);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [lookupKey, authLoading]);

  const bySlug = useMemo(() => {
    const map: Record<string, ServiceAssignment> = {};
    assignments.forEach((a) => {
      if (a.service_slug) map[a.service_slug] = a;
    });
    return map;
  }, [assignments]);

  const canAccess = useCallback(
    (slug?: string) => {
      if (!slug) return true;
      if (isIndividual) {
        // Fail closed (the server enforces it too), but never hide own records.
        return slug === 'health-records' || Boolean(bySlug[slug]);
      }
      if (!corporateId || lookupFailed) return true;
      return Boolean(bySlug[slug]);
    },
    [bySlug, corporateId, lookupFailed, isIndividual],
  );

  const getAssignment = useCallback(
    (slug?: string) => (slug ? bySlug[slug] : undefined),
    [bySlug],
  );

  const value = useMemo(
    () => ({ ready, bySlug, canAccess, getAssignment }),
    [ready, bySlug, canAccess, getAssignment],
  );

  return <ServiceAccessContext.Provider value={value}>{children}</ServiceAccessContext.Provider>;
};

export function useServiceAccess(): ServiceAccessValue {
  const ctx = useContext(ServiceAccessContext);
  if (!ctx) {
    return {
      ready: true,
      bySlug: {},
      canAccess: () => true,
      getAssignment: () => undefined,
    };
  }
  return ctx;
}

export default ServiceAccessProvider;